import React, { useState } from "react";
import { type ImageUpload, ProfileComponentProps } from "../types";
import { fetchExistingProfileImage } from "../requests";
import "./components.css";
import animationData from "../assets/spinner-animation.json";
import Lottie from "react-lottie";
import DefaultIcon from "../assets/op_o.png";
import { useThemeColors } from "../contexts";
import { useStatsigClient } from "../hooks";

interface Props {
  id: string;
  allowEdit: boolean;
  photoAssetId?: string;
  isLoading: boolean;
  round: boolean;
  onImageSelected: (imageUpload: ImageUpload) => void;
  onExistingImageSelected?: (imageUpload: ImageUpload) => void;
  borderColor?: string;
  useBorder?: boolean;
}

export default function ProfilePhoto(props: Props) {
  const [hover, setHover] = useState<boolean>(false);
  const { primary } = useThemeColors();
  const statsigClient = useStatsigClient();

  const defaultImagesAssetIds = [
    "892e8430-e562-4d9d-94ff-803a99e1abb7",
    "503a6c23-da07-4ba3-9fb6-74cd40cf5ca3",
    "76c9f42e-7731-4fa5-b9d6-ffef76bb6ac9",
    "100e4deb-d88a-4b02-93a2-67f2b322803b",
    "cb53b9b5-c2ed-4efc-96d7-37aa261c5b19",
  ];

  const handleExistingImageClick = async () => {
    if (!props.photoAssetId || !props.onExistingImageSelected) return;

    try {
      const imageUpload = await fetchExistingProfileImage(props.photoAssetId);
      props.onExistingImageSelected!(imageUpload);
    } catch (error) {
      console.error("Failed to convert existing image:", error);
    }
  };

  // Check feature flag to override useBorder prop
  const shouldUseBorder =
    props.useBorder &&
    statsigClient?.checkGate("enable_branding_border") !== false;

  if (!props.allowEdit) {
    return (
      <img
        alt="profile-photo"
        crossOrigin="anonymous"
        className={`w-[110px] h-[110px] object-cover ${props.round ? "rounded-full" : "rounded-lg"} ${shouldUseBorder ? "border-4" : ""}`}
        style={{ borderColor: shouldUseBorder ? primary : "transparent" }}
        src={
          props.photoAssetId
            ? `https://pub-4b15203a4a0f4b26ab71deeffce0164a.r2.dev/${props.photoAssetId}?nocache=12345`
            : DefaultIcon
        }
      />
    );
  }

  return (
    <div
      className={
        "min-w-[110px] min-h-[110px] max-w-[110px] max-h-[110px] border-op-dark relative overflow-hidden" +
        (props.allowEdit ? " hover:border-2" : "") +
        (props.round ? " rounded-[55px]" : "")
      }
      onClick={() => {
        if (!props.allowEdit) {
          return;
        }
        if (
          props.photoAssetId &&
          !defaultImagesAssetIds.find((id) => id === props.photoAssetId) &&
          props.onExistingImageSelected
        ) {
          handleExistingImageClick();
        } else {
          document.getElementById("logo-uploader-" + props.id)?.click();
        }
      }}
      onMouseEnter={() => {
        if (!props.allowEdit) {
          return;
        }
        setHover(true);
      }}
      onMouseLeave={() => setHover(false)}
    >
      {!props.isLoading ? (
        <div>
          <img
            alt="profile-photo"
            className="object-cover min-h-[110px]"
            src={
              props.photoAssetId
                ? "https://pub-4b15203a4a0f4b26ab71deeffce0164a.r2.dev/" +
                  props.photoAssetId
                : DefaultIcon
            }
          />

          <input
            className="hidden"
            type="file"
            id={"logo-uploader-" + props.id}
            onChange={(event) => {
              if (event.target.files === null) return;

              const file = event.target.files[0];
              const reader = new FileReader();

              reader.onload = (e: ProgressEvent<FileReader>) => {
                const formatInfo = (e.target?.result as string).split(",")[0];
                const base64String = (e.target?.result as string).split(",")[1];
                const format = formatInfo.split(";")[0].split(":")[1];

                const imageUpload: ImageUpload = {
                  fileType: formatInfo,
                  base64File: base64String,
                };
                props.onImageSelected(imageUpload);
              };

              reader.readAsDataURL(file);
            }}
          ></input>
        </div>
      ) : (
        <div>
          <Lottie
            style={{ width: 80 }}
            options={{ animationData: animationData }}
          />
        </div>
      )}
    </div>
  );
}
